/**
 * Sunday Golf Custom Mega Menu
 * 3-column progressive disclosure: Items → Children → Grandchildren
 */

(function() {
  'use strict';

  class SundayMegaMenu {
    constructor(element) {
      this.element = element;
      this.menuId = element.dataset.megaMenuId;

      // Column 1 items (top-level)
      this.level1Items = element.querySelectorAll('.sunday-mega-menu__item[data-level="1"]');

      // Column 2 panels and items
      this.level2Panels = element.querySelectorAll('.sunday-mega-menu__panel[data-level="2"]');
      this.level2Col = element.querySelector('.sunday-mega-menu__col--level-2');

      // Column 3 panels
      this.level3Panels = element.querySelectorAll('.sunday-mega-menu__panel[data-level="3"]');
      this.level3Col = element.querySelector('.sunday-mega-menu__col--level-3');

      // Empty-state banners
      this.col2Banner = element.querySelector('.sunday-mega-menu__col--level-2 .sunday-mega-menu__empty-banner');
      this.col3Banner = element.querySelector('.sunday-mega-menu__col--level-3 .sunday-mega-menu__empty-banner');

      this.activeLevel1 = null;
      this.activeLevel2 = null;

      // Track which level-1 item was clicked (for double-click-to-navigate)
      this.clickedItemIndex = null;

      this.init();
    }

    init() {
      // Level 1: Hover shows Column 2 panel
      this.level1Items.forEach((item) => {
        const link = item.querySelector('.sunday-mega-menu__link');
        const hasChildren = link && link.dataset.hasChildren === 'true';

        item.addEventListener('mouseenter', () => {
          if (hasChildren) {
            this.showLevel2Panel(item.dataset.itemIndex);
          } else {
            this.hideAllLevel2Panels();
            this.showBanner(2);
          }
          this.setActiveItem(item, 1);
          // Reset click state when hovering a different item
          if (this.clickedItemIndex !== item.dataset.itemIndex) {
            this.clickedItemIndex = null;
          }
        });

        // Click: first click opens panel, second click navigates
        if (link) {
          link.addEventListener('click', (e) => {
            const href = link.getAttribute('href');

            if (hasChildren) {
              if (this.clickedItemIndex === item.dataset.itemIndex) {
                // Second click — navigate
                if (href && href !== '#' && href !== '') {
                  return; // allow default navigation
                }
              }
              // First click — open panel, prevent navigation
              e.preventDefault();
              this.showLevel2Panel(item.dataset.itemIndex);
              this.setActiveItem(item, 1);
              this.clickedItemIndex = item.dataset.itemIndex;
            } else {
              // No children — navigate normally
              if (!href || href === '#' || href === '') {
                e.preventDefault();
              }
            }
          });
        }
      });

      // Level 2: Hover shows Column 3 panel
      this.level2Panels.forEach((panel) => {
        const items = panel.querySelectorAll('.sunday-mega-menu__item[data-level="2"]');

        items.forEach((item) => {
          const link = item.querySelector('.sunday-mega-menu__link');
          const hasChildren = link && link.dataset.hasChildren === 'true';
          const parentIndex = item.dataset.parentIndex;
          const itemIndex = item.dataset.itemIndex;

          item.addEventListener('mouseenter', () => {
            if (hasChildren) {
              this.showLevel3Panel(parentIndex, itemIndex);
            } else {
              this.hideAllLevel3Panels();
              this.showBanner(3);
            }
            this.setActiveItem(item, 2);
          });

          // Click: if has children, show level 3 instead of navigating
          if (link) {
            link.addEventListener('click', (e) => {
              if (hasChildren) {
                e.preventDefault();
                this.showLevel3Panel(parentIndex, itemIndex);
                this.setActiveItem(item, 2);
              }
            });
          }
        });
      });

      // Close panels when mouse leaves the mega menu
      this.element.addEventListener('mouseleave', () => {
        this.closeAll();
      });
    }

    showLevel2Panel(index) {
      this.hideAllLevel2Panels();
      this.hideAllLevel3Panels();

      const panel = this.element.querySelector(`.sunday-mega-menu__panel[data-panel-for="${index}"][data-level="2"]`);
      if (panel) {
        panel.setAttribute('aria-hidden', 'false');
        this.activeLevel1 = index;
        this.hideBanner(2);
      }

      // Also hide col3 banner when col2 panel opens (col3 has no panel yet)
      this.showBanner(3);
    }

    hideAllLevel2Panels() {
      this.level2Panels.forEach((panel) => {
        panel.setAttribute('aria-hidden', 'true');
      });
      const level2Items = this.element.querySelectorAll('.sunday-mega-menu__item[data-level="2"]');
      level2Items.forEach((item) => item.classList.remove('is-active'));
      this.activeLevel1 = null;
    }

    showLevel3Panel(parentIndex, itemIndex) {
      this.hideAllLevel3Panels();

      const panelKey = `${parentIndex}-${itemIndex}`;
      const panel = this.element.querySelector(`.sunday-mega-menu__panel[data-panel-for="${panelKey}"][data-level="3"]`);
      if (panel) {
        panel.setAttribute('aria-hidden', 'false');
        this.activeLevel2 = panelKey;
        this.hideBanner(3);
      }
    }

    hideAllLevel3Panels() {
      this.level3Panels.forEach((panel) => {
        panel.setAttribute('aria-hidden', 'true');
      });
      this.activeLevel2 = null;
    }

    hideBanner(col) {
      const banner = col === 2 ? this.col2Banner : this.col3Banner;
      if (banner) banner.classList.add('is-hidden');
    }

    showBanner(col) {
      const banner = col === 2 ? this.col2Banner : this.col3Banner;
      if (banner) banner.classList.remove('is-hidden');
    }

    setActiveItem(item, level) {
      const siblings = item.parentElement.querySelectorAll('.sunday-mega-menu__item');
      siblings.forEach((sib) => sib.classList.remove('is-active'));

      item.classList.add('is-active');

      const link = item.querySelector('.sunday-mega-menu__link[data-has-children]');
      if (link) {
        siblings.forEach((sib) => {
          const sibLink = sib.querySelector('.sunday-mega-menu__link[data-has-children]');
          if (sibLink) sibLink.setAttribute('aria-expanded', 'false');
        });
        link.setAttribute('aria-expanded', 'true');
      }
    }

    closeAll() {
      this.hideAllLevel2Panels();
      this.hideAllLevel3Panels();

      // Show banners with fade
      this.showBanner(2);
      this.showBanner(3);

      // Clear click state
      this.clickedItemIndex = null;

      const allItems = this.element.querySelectorAll('.sunday-mega-menu__item');
      allItems.forEach((item) => item.classList.remove('is-active'));

      const allLinks = this.element.querySelectorAll('.sunday-mega-menu__link[data-has-children]');
      allLinks.forEach((link) => link.setAttribute('aria-expanded', 'false'));
    }
  }

  // Initialize all mega menus on the page
  function initSundayMegaMenus() {
    const menus = document.querySelectorAll('.sunday-mega-menu');

    menus.forEach((menu) => {
      if (!menu.dataset.initialized) {
        new SundayMegaMenu(menu);
        menu.dataset.initialized = 'true';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSundayMegaMenus);
  } else {
    initSundayMegaMenus();
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            const menus = node.querySelectorAll ? node.querySelectorAll('.sunday-mega-menu:not([data-initialized])') : [];
            if (menus.length || (node.classList && node.classList.contains('sunday-mega-menu') && !node.dataset.initialized)) {
              initSundayMegaMenus();
            }
          }
        });
      }
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', initSundayMegaMenus);

  window.SundayMegaMenu = SundayMegaMenu;
  window.initSundayMegaMenus = initSundayMegaMenus;

})();
